import { useErrorHandler } from '@/components/ui/ErrorHandlerProvider';
import { Tooltip } from '@/components/ui/Tooltip';
import { useTopToast } from '@/components/ui/TopToastProvider';
import HomeDefaultDashboardTooltipIcon from '@/assets/images/icons/home_default_dashboard_tooltip.svg';
import HomeActiveDashboardIcon from '@/assets/images/icons/home_active_dashboard.svg';
import HomeDefaultDashboardIcon from '@/assets/images/icons/home_default_dashboard.svg';
import HomeHoverDashboardIcon from '@/assets/images/icons/home_hover_dashboard.svg';
import { useSetHomeDashboardFlow } from '@/hooks/useSetHomeDashboardFlow';
import { parseHomeSurveyConfig } from '@/lib/dashboardHomeSurvey';
import { isUserHomeCourseSlug } from '@/lib/userHomeUrl';
import { dashboardToolbarArtworkButton, DASHBOARD_TOOLBAR_ARTWORK_PX } from '@/lib/dashboardToolbarUi';
import {
  SET_HOME_ACCENT_PANEL_BG,
  SET_HOME_TOAST_ICON_PX,
  SET_HOME_TOAST_MAX_WIDTH_PX,
} from '@/lib/setHomeDashboardUi';
import { LOADING_SPINNER_COLOR } from '@/lib/loadingSpinnerColor';
import type { CourseMetadata } from '@/types';
import type { MembershipTier } from '@/types/live-session';
import { useAuth } from '@/lib/AuthContext';
import type { UserPreferenceMetadata } from '@/lib/userPreferences';
import type { ViewStyle } from 'react-native';
import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ActivityIndicator, Platform, Pressable } from 'react-native';
import { Button, SizableText, XStack, YStack } from 'tamagui';
import { BodyText, CardTitle } from '@/components/ui/Typography';

const SET_HOME_CURRENT_HOME_TOAST_MS = 4_000;
const SET_HOME_SUCCESS_TOAST_MS = 5_000;
const SET_HOME_CONFIRM_TOAST_MS = 5_000;

const SET_HOME_TOAST_PRIMARY_BTN_TEXT = {
  fontFamily: '$body',
  fontSize: 14,
  lineHeight: 20,
  fontWeight: '600',
  letterSpacing: 0,
} as const;

function setHomeToastPillShell(pressed: boolean, variant: 'primary' | 'cancel'): ViewStyle {
  const base: ViewStyle = {
    alignSelf: 'flex-start',
    height: 32,
    paddingVertical: 8,
    paddingLeft: 16,
    paddingRight: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 30,
    borderWidth: 1,
    opacity: pressed ? 0.92 : 1,
  };

  if (variant === 'cancel') {
    return {
      ...base,
      borderColor: 'rgba(255,255,255,0.55)',
      backgroundColor: 'transparent',
    };
  }

  return {
    ...base,
    borderColor: '#C9C5C3',
    backgroundColor: '#FFF',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12), 0 0 2px 0 rgba(0, 0, 0, 0.08)',
        } as const)
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 4,
          elevation: 2,
        }),
  };
}

function SetHomeToastPillButton({
  variant,
  label,
  letterSpacing = 0,
  onPress,
  disabled,
  accessibilityLabel,
  minWidth,
  children,
}: {
  variant: 'primary' | 'cancel';
  label?: string;
  letterSpacing?: number;
  onPress: () => void | Promise<void>;
  disabled?: boolean;
  accessibilityLabel?: string;
  minWidth?: number;
  children?: ReactNode;
}) {
  return (
    <Pressable
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled: !!disabled }}
      onPress={() => void onPress()}
      style={({ pressed }) => ({
        ...setHomeToastPillShell(pressed && !disabled, variant),
        ...(minWidth != null ? { minWidth } : {}),
        ...(disabled ? { opacity: 0.45 } : {}),
      })}
    >
      {children ?? (
        <SizableText
          color={variant === 'cancel' ? '#FFF' : '#0D0D0D'}
          {...SET_HOME_TOAST_PRIMARY_BTN_TEXT}
          letterSpacing={letterSpacing}
        >
          {label}
        </SizableText>
      )}
    </Pressable>
  );
}

function useHomeDashboardSurveyState(
  courseMetadata: CourseMetadata | null | undefined,
  courseSlug: string,
  preferenceMetadata: UserPreferenceMetadata | null,
  user: ReturnType<typeof useAuth>['user'],
  membershipTier: MembershipTier,
  membershipLoading: boolean,
) {
  const config = useMemo(() => parseHomeSurveyConfig(courseMetadata), [courseMetadata]);
  const isEligible =
    Platform.OS === 'web' && !!user && !membershipLoading && config !== null;
  const isCurrentHome = useMemo(
    () =>
      Boolean(
        user &&
        courseSlug &&
        isUserHomeCourseSlug(
          preferenceMetadata,
          membershipTier,
          membershipLoading,
          courseSlug,
        ),
      ),
    [user, courseSlug, preferenceMetadata, membershipTier, membershipLoading],
  );

  return { config, isEligible, isCurrentHome };
}

function accentShell(children: ReactNode) {
  return (
    <YStack
      width="100%"
      backgroundColor={SET_HOME_ACCENT_PANEL_BG}
      borderRadius={8}
      borderWidth={1}
      borderColor="rgba(255,255,255,0.14)"
      overflow="hidden"
    >
      {children}
    </YStack>
  );
}

function CurrentHomeDashboardToastBody({ onGotIt }: { onGotIt: () => void }) {
  return (
    <>
      <YStack paddingHorizontal={16} paddingTop={12} paddingBottom={8} gap={8}>
        <CardTitle color="#FFF">Current Home Dashboard.</CardTitle>
        <BodyText color="#FFF" fontWeight="600">
          To set a different Home, go to the other dashboard and use this button there.
        </BodyText>
      </YStack>
      <YStack paddingHorizontal={16} paddingTop={0} paddingBottom={16}>
        <XStack jc="flex-end">
          <SetHomeToastPillButton variant="primary" label="Got it" onPress={onGotIt} />
        </XStack>
      </YStack>
    </>
  );
}

function SetHomeSuccessToastBody({
  courseTitle,
  onGotIt,
}: {
  courseTitle: string;
  onGotIt: () => void;
}) {
  return (
    <XStack
      width="100%"
      maxWidth={SET_HOME_TOAST_MAX_WIDTH_PX}
      paddingTop={12}
      paddingRight={16}
      paddingBottom={16}
      paddingLeft={16}
      gap={10}
      ai="flex-start"
    >
      <YStack accessibilityElementsHidden>
        <HomeDefaultDashboardTooltipIcon
          width={SET_HOME_TOAST_ICON_PX}
          height={SET_HOME_TOAST_ICON_PX}
        />
      </YStack>
      <YStack flex={1} gap={8} minWidth={0} ai="flex-start">
        <CardTitle color="#FFF">Home page is updated!</CardTitle>
        <BodyText color="#FFF" fontWeight="600" numberOfLines={3}>
          {`${courseTitle} is now your default dashboard.`}
        </BodyText>
        <SetHomeToastPillButton variant="primary" label="Got it" onPress={onGotIt} />
      </YStack>
    </XStack>
  );
}

export type SetHomeDashboardButtonProps = {
  courseTitle: string;
  courseSlug: string;
  courseMetadata: CourseMetadata | null | undefined;
};

export function SetHomeDashboardButton({
  courseTitle,
  courseSlug,
  courseMetadata,
}: SetHomeDashboardButtonProps) {
  const { showError } = useErrorHandler();
  const { showTopToast, dismissTopToast } = useTopToast();
  const {
    user,
    membershipTier,
    membershipLoading,
    preferenceMetadata,
    refreshUserPreferences,
    applyPreferenceMetadata,
  } = useAuth();
  const { config, isEligible, isCurrentHome } = useHomeDashboardSurveyState(
    courseMetadata,
    courseSlug,
    preferenceMetadata,
    user,
    membershipTier,
    membershipLoading,
  );

  const { busy, confirmSetHome } = useSetHomeDashboardFlow({
    user,
    config: config ?? null,
    onError: showError,
    refreshUserPreferences,
    applyPreferenceMetadata,
  });

  const [dashboardHomeHovered, setDashboardHomeHovered] = useState(false);

  useEffect(() => {
    if (busy || isCurrentHome) setDashboardHomeHovered(false);
  }, [busy, isCurrentHome]);

  const showSuccessToast = useCallback(() => {
    showTopToast({
      autoDismissMs: SET_HOME_SUCCESS_TOAST_MS,
      maxWidth: SET_HOME_TOAST_MAX_WIDTH_PX,
      content: accentShell(
        <SetHomeSuccessToastBody courseTitle={courseTitle} onGotIt={dismissTopToast} />,
      ),
    });
  }, [courseTitle, dismissTopToast, showTopToast]);

  const openInfoToast = useCallback(() => {
    showTopToast({
      autoDismissMs: SET_HOME_CURRENT_HOME_TOAST_MS,
      content: accentShell(<CurrentHomeDashboardToastBody onGotIt={dismissTopToast} />),
    });
  }, [dismissTopToast, showTopToast]);

  const openConfirmToast = useCallback(() => {
    showTopToast({
      autoDismissMs: SET_HOME_CONFIRM_TOAST_MS,
      content: accentShell(
        <>
          <YStack paddingHorizontal={16} paddingTop={12} paddingBottom={16}>
            <CardTitle color="#FFF">Set this dashboard as your Home?</CardTitle>
          </YStack>
          <YStack paddingHorizontal={16} paddingBottom={16} paddingTop={0}>
            <XStack gap="$3" jc="flex-end" flexWrap="wrap">
              <SetHomeToastPillButton
                variant="cancel"
                label="Cancel"
                disabled={busy}
                onPress={dismissTopToast}
              />
              <SetHomeToastPillButton
                variant="primary"
                disabled={busy}
                minWidth={148}
                accessibilityLabel="Set as Home"
                onPress={async () => {
                  dismissTopToast();
                  const r = await confirmSetHome();
                  if (!r.ok) return;
                  showSuccessToast();
                }}
              >
                {busy ? (
                  <ActivityIndicator color={LOADING_SPINNER_COLOR} size="small" />
                ) : (
                  <SizableText color="#0D0D0D" {...SET_HOME_TOAST_PRIMARY_BTN_TEXT}>
                    Set as Home
                  </SizableText>
                )}
              </SetHomeToastPillButton>
            </XStack>
          </YStack>
        </>,
      ),
    });
  }, [busy, confirmSetHome, dismissTopToast, showSuccessToast, showTopToast]);

  const handleTriggerPress = () => {
    if (!config) return;
    if (isCurrentHome) openInfoToast();
    else openConfirmToast();
  };

  if (!isEligible || !config) return null;

  const a11yLabel = isCurrentHome
    ? 'Current home dashboard. Opens info.'
    : 'Set this dashboard as your home';

  const homeButton = (
    <Button
      {...dashboardToolbarArtworkButton}
      disabled={busy}
      onPress={handleTriggerPress}
      accessibilityLabel={a11yLabel}
      accessibilityRole="button"
      onHoverIn={() => {
        if (Platform.OS === 'web' && !isCurrentHome) setDashboardHomeHovered(true);
      }}
      onHoverOut={() => setDashboardHomeHovered(false)}
      pressStyle={{ opacity: 0.92 }}
    >
      {busy ? (
        <ActivityIndicator size="small" color={LOADING_SPINNER_COLOR} />
      ) : isCurrentHome ? (
        <HomeActiveDashboardIcon
          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
          accessibilityElementsHidden
        />
      ) : dashboardHomeHovered && Platform.OS === 'web' ? (
        <HomeHoverDashboardIcon
          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
          accessibilityElementsHidden
        />
      ) : (
        <HomeDefaultDashboardIcon
          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
          accessibilityElementsHidden
        />
      )}
    </Button>
  );

  return Platform.OS === 'web' && !isCurrentHome && !busy ? (
    <Tooltip text={a11yLabel} placement="top" tone="accent">
      {homeButton}
    </Tooltip>
  ) : (
    homeButton
  );
}
